/**
 * AnalysisPage.jsx
 * ================
 * Full telemetry analysis view for a single session, with optional cross-session
 * comparison ("Compare With..." overlay). Shows speed traces, G-force, lap delta,
 * split times, corner breakdown, track heatmap, and track map.
 *
 * KEY CONCEPTS:
 *
 * 1. PRIMARY SESSION — The session the user clicked on from SessionsPage.
 *    Loaded via /api/session/:uid/:fileName (or /api/sharing/session for shared).
 *    Stored in `sessionData`. Laps are filtered by LAP_FILTER_THRESHOLD to remove
 *    outliers (e.g., in-laps, out-laps) and stored in `filteredLaps`.
 *
 * 2. OVERLAY SESSION — An optional second session for comparison, loaded when the
 *    user clicks "Compare With..." and picks a session at the same track.
 *    Stored in `overlaySessionData`. Can be the user's own session or a shared
 *    teammate's session.
 *
 * 3. GPS ALIGNMENT — When comparing sessions from different data systems (e.g.,
 *    AIM vs MoTeC), each logger may start recording at a different physical
 *    position on the track. `alignLapToReference()` shifts the overlay lap's
 *    distance axis so both traces start at the same GPS location.
 *    - `data` = positive-dist points only (for telemetry graph rendering)
 *    - `fullData` = all points including negative dist (for split time / corner
 *      breakdown GPS matching, which needs the full track coverage)
 *
 * 4. DISPLAY LAPS — The merged array of selected primary laps + selected overlay
 *    laps (GPS-aligned). This is what gets passed to all visualization components.
 *    Each lap is tagged with `driverLabel` (first name) for compare mode display.
 *
 * 5. LAP DELTA — Shown when exactly 2 laps are displayed (either 2 from same
 *    session, or 1 primary + 1 overlay). Computes time delta vs distance using
 *    interpolation, only over the overlapping distance range.
 *
 * 6. LAP FILTERING — Laps more than LAP_FILTER_THRESHOLD (30%) away from the
 *    best lap time are hidden from the lap selector to remove outliers.
 *    `selectedLaps` stores indices into `filteredLaps` (not the raw session laps).
 */

import React, { useState, useMemo, useEffect, useCallback, useRef, startTransition } from 'react';
import ReactDOM from 'react-dom';
import Header from '../components/Header';
import { SessionTypeIcon } from '../utils/RaceImageMapper';
import StatCard from '../components/StatCard';
import TelemetryGraph from '../components/TelemetryGraph';
import TrackCanvas from '../components/TrackCanvas';
import GForcePlot from '../components/GForcePlot';
import LapDelta from '../components/LapDelta';
import LapSelector from '../components/LapSelector';
import ResizableChart from '../components/ResizableChart';
import SplitTimeTable from '../components/SplitTimeTable';
import CornerBreakdown from '../components/CornerBreakdown';
import TrackHeatmap from '../components/TrackHeatmap';
import BrakingSummaryTable from '../components/BrakingSummaryTable';
import ThrottleSummaryTable from '../components/ThrottleSummaryTable';
import BrakingCoachCard from '../components/BrakingCoachCard';
import { CHANNELS, ALL_CHANNELS, getChannel, getAllChannels } from '../utils/constants';
import { generateSessionData, getMaxSpeed, getMaxG, applySmoothedSamples } from '../utils/telemetry';
import { formatLapTime, formatDate, formatSpeed, formatG } from '../utils/format';
import { getMedianLapDist, findBestLapIndex } from '../utils/bestLap';
import { getTimezone, formatTimeAtTrack } from '../utils/timezone';
import { buildSplitTimeReport, findClosestSampleIndex } from '../utils/splitTimes';
import { parseSegments } from '../utils/trackRenderer';
import { analyzeBraking } from '../utils/brakingAnalysis';
import { analyzeThrottle, hasThrottleData } from '../utils/throttleAnalysis';
import { buildBrakingCoachCards } from '../utils/coaching/brakingInsights';
import { buildThrottleCoachCards } from '../utils/coaching/throttleInsights';
import { buildSegmentSpeedCoachCards } from '../utils/coaching/segmentSpeedCoach';
import { buildSegmentTimeCoachCards } from '../utils/coaching/segmentTimeCoach';
import { authHeaders } from '../utils/authService';
import { exportSessionCsv } from '../utils/exportCsv';
import { getSharedSessions, getSharedSessionData } from '../utils/sharingService';
import { WeatherIcon, formatTemp } from '../utils/weather';
import VideoPlayer from '../components/VideoPlayer';
import ChartSlot from '../components/ChartSlot';
import VoiceConfigPanel from '../components/VoiceConfigPanel';
import SatelliteMap from '../components/SatelliteMap';
import LoadingProgress from '../components/LoadingProgress';

// Colors assigned to each lap trace in order (wraps around for >10 laps)
const TRACE_COLORS = [
  '#2e86c1', '#e74c3c', '#27ae60', '#f39c12', '#9b59b6',
  '#1abc9c', '#e67e22', '#3498db', '#e91e63', '#00bcd4',
];

/**
 * GPS ALIGNMENT FOR CROSS-SESSION COMPARISON
 * ===========================================
 * When comparing laps from different data systems (e.g., AIM logger vs MoTeC),
 * each system starts recording at a slightly different physical position on the
 * track. This means dist=0 in session A might be at Turn 1, while dist=0 in
 * session B is at the pit exit — making the telemetry traces misaligned.
 *
 * This function fixes that by:
 * 1. Taking the reference lap's first GPS point (where the primary session starts)
 * 2. Finding the closest GPS point in the overlay lap's data
 * 3. Shifting the overlay's dist/time so that matched point becomes dist=0, time=0
 *
 * The result is two data properties:
 *   - data:     Only points with dist >= 0 (for telemetry graph rendering,
 *               which needs monotonically increasing positive distances)
 *   - fullData: ALL points including negative dist (for split time and corner
 *               breakdown calculations, which match GPS coordinates and need
 *               full track coverage regardless of distance values)
 *
 * CANDIDATE SELECTION STRATEGY:
 * On a closed circuit, the start/finish GPS coordinate appears multiple times
 * per lap (the car passes it once). If we just pick the absolute closest GPS
 * match, we might match a point near the END of the overlay data — leaving
 * only a few positive-dist points and losing most of the telemetry.
 *
 * Instead, we rank all candidate points by GPS closeness, then pick the best
 * one that preserves at least 75% of the data. Fallback: closest in first half.
 *
 * @param {Object} overlayLap - Lap from the comparison session (with .data array)
 * @param {Object} refLap     - Lap from the primary session (alignment target)
 * @returns {Object} Aligned lap with .data (positive dist) and .fullData (all)
 */
function alignLapToReference(overlayLap, refLap) {
  if (!overlayLap?.data?.length || !refLap?.data?.length) return overlayLap;

  const refStart = refLap.data[0];
  if (refStart.lat == null || refStart.lng == null) return overlayLap;

  // Step 1: Compute Haversine GPS distance from every overlay point to the
  //         reference lap's start position. Store as {idx, dist} candidates.
  const toRad = (v) => (v * Math.PI) / 180;
  const candidates = [];
  for (let i = 0; i < overlayLap.data.length; i++) {
    const s = overlayLap.data[i];
    if (s.lat == null || s.lng == null) continue;
    const dLat = toRad(s.lat - refStart.lat);
    const dLng = toRad(s.lng - refStart.lng);
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(refStart.lat)) * Math.cos(toRad(s.lat)) * Math.sin(dLng / 2) ** 2;
    const d = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    candidates.push({ idx: i, dist: d });
  }

  // Step 2: Sort candidates by GPS distance — closest match first
  candidates.sort((a, b) => a.dist - b.dist);

  // Step 3: Pick the best candidate that keeps at least 75% of data.
  //         "Remaining" = points from candidate index to end of array.
  //         Points before the candidate get negative dist after shifting.
  const minKeep = overlayLap.data.length * 0.75;
  let bestIdx = 0;
  for (const c of candidates) {
    const remaining = overlayLap.data.length - c.idx;
    if (remaining >= minKeep) {
      bestIdx = c.idx;
      break;
    }
  }

  // Step 4: Fallback — if no candidate preserves 75%, use the absolute
  //         closest match in the first half of the data
  if (bestIdx === 0 && candidates.length) {
    const halfLen = Math.floor(overlayLap.data.length / 2);
    const firstHalf = candidates.filter((c) => c.idx <= halfLen);
    if (firstHalf.length) bestIdx = firstHalf[0].idx;
  }

  // Step 5: Shift dist and time so the alignment point becomes origin (0, 0).
  const distOffset = overlayLap.data[bestIdx].dist;
  const timeOffset = overlayLap.data[bestIdx].time;
  const allAligned = overlayLap.data.map((pt) => ({
    ...pt,
    dist: Math.round((pt.dist - distOffset) * 10000) / 10000,
    time: pt.time - timeOffset,
  }));

  // Step 6: Split into two arrays:
  //   positiveData = dist >= 0 only (for telemetry graph x-axis rendering)
  //   allAligned   = everything (for split times/corners GPS coordinate matching)
  const positiveData = allAligned.filter((pt) => pt.dist >= 0);

  // Step 7: Safety check — if alignment discarded more than 50% of data,
  //         something went wrong; return the original unaligned lap
  if (positiveData.length < overlayLap.data.length * 0.5) {
    return overlayLap;
  }

  return {
    ...overlayLap,
    data: positiveData,   // Used by TelemetryGraph, LapDelta, car positions
    fullData: allAligned,  // Used by SplitTimeTable, CornerBreakdown (via buildEntryForSegment)
  };
}


// Height of each telemetry trace chart (in pixels)
const TRACE_HEIGHT = 320;

// Maximum % deviation from best lap to include in lap selector (0.30 = 30%)
const LAP_FILTER_THRESHOLD = 0.30;

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--color-bg)',
  },
  content: {
    width: '100%',
    minWidth: 1920,
    maxWidth: 'none',
    margin: '0 auto',
    padding: '24px',
    boxSizing: 'border-box',
  },
  sessionInfo: {
    marginBottom: 20,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  trackName: {
    fontFamily: 'var(--font-heading)',
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--color-text)',
    letterSpacing: 1,
  },
  carDate: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-text-dim)',
    marginTop: 4,
  },
  statsRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  graphsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 12,
  },
  sectionTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  sectionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 12,
    marginBottom: 12,
    flexWrap: 'nowrap',
    minWidth: 0,
  },
  heightControlWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'nowrap',
    flexShrink: 0,
  },
  viewStyleLabel: {
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  viewStyleSelect: {
    height: 32,
    padding: '0 30px 0 10px',
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  viewResetBtn: {
    padding: '6px 12px',
    background: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  xAxisToggleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    padding: 0,
    background: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text)',
    cursor: 'pointer',
  },
  loading: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-text-dim)',
    textAlign: 'center',
    padding: 60,
  },
  mainLayout: {
    display: 'flex',
    gap: 24,
    alignItems: 'flex-start',
    width: '100%',
  },
  analysisColumn: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    width: '100%',
  },
  topRow: {
    display: 'flex',
    gap: 24,
    alignItems: 'stretch',
    width: '100%',
  },
  tracesColumn: {
    flex: '1 1 0',
    width: 'auto',
    minWidth: 0,
  },
  fullWidthSection: {
    width: '100%',
  },
  mediaCoachRow: {
    display: 'flex',
    gap: 16,
    alignItems: 'stretch',
    width: '100%',
    flexWrap: 'wrap',
  },
  mediaPanel: {
    flex: '1 1 620px',
    minWidth: 0,
  },
  framedPanel: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    padding: 14,
    height: '100%',
    boxSizing: 'border-box',
  },
  tabRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  tabButton: {
    padding: '6px 16px',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 6,
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-heading)',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  halfWidthSection: {
    minWidth: 0,
  },
  trackMapColumn: {
    flex: '0 0 clamp(280px, 25vw, 420px)',
    minWidth: 280,
    maxWidth: 420,
    width: 'clamp(280px, 25vw, 420px)',
  },
  coachingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 12,
  },
  helperText: {
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    lineHeight: 1.5,
  },
  embeddedCoachSection: {
    marginTop: 18,
    paddingTop: 18,
    borderTop: '1px solid var(--color-border)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  embeddedCoachTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 1.8,
    textTransform: 'uppercase',
  },
};

export default function AnalysisPage({ user, session, onBack, onLogout, onAdmin, onDashboard, onProfile }) {
  // ── Primary session state ──
  const [sessionData, setSessionData] = useState(null);        // Full session with laps[] from API
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);            // Error message if session fetch failed
  const [trackCoordinates, setTrackCoordinates] = useState(null); // Track GPS points for map/heatmap
  const [trackMirror, setTrackMirror] = useState({ mirrorX: false, mirrorY: false });
  const [selectedLaps, setSelectedLaps] = useState([]);        // Indices into filteredLaps (not raw laps)
  const [lapSortOrder, setLapSortOrder] = useState('default'); // 'default', 'fastest', 'slowest'
  const [traceViewStyle, setTraceViewStyle] = useState('standard'); // 'standard', 'candles', 'ribbon'
  const [xAxisMode, setXAxisMode] = useState('distance'); // 'distance' or 'time'
  const [crosshairPos, setCrosshairPos] = useState(null);      // 0-1 normalized position on x-axis
  const [zoomRange, setZoomRange] = useState(null);            // { x0, x1 } normalized zoom window
  const [dragRange, setDragRange] = useState(null);            // { x0, x1 } drag selection for highlight
  const [tracesWidth, setTracesWidth] = useState(700);         // Pixel width of trace column (responsive)
  const [trackMapWidth, setTrackMapWidth] = useState(420);     // Pixel width of track map column
  const [traceSamplingMode, setTraceSamplingMode] = useState('fit');

  // ── Overlay / compare session state ──
  const [overlaySessionData, setOverlaySessionData] = useState(null); // Second session for comparison
  const [overlayLabel, setOverlayLabel] = useState('');        // "DriverName - Date Time" label
  const [selectedOverlayLaps, setSelectedOverlayLaps] = useState([]); // Indices into overlaySessionData.laps
  const [compareSessions, setCompareSessions] = useState([]);  // Available sessions for comparison
  const [compareLoading, setCompareLoading] = useState(false);
  // ── Session notes state ──
  const [notes, setNotes] = useState('');
  const [savedNotes, setSavedNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  // ── Session video state ──
  const [primaryVideos, setPrimaryVideos] = useState([]);
  const [overlayVideos, setOverlayVideos] = useState([]);

  // ── Selectable chart slot state ──
  const [chart2Key, setChart2Key] = useState(null);
  const [chart3Key, setChart3Key] = useState(null);
  const [chart4Key, setChart4Key] = useState(null);
  const [chart2Collapsed, setChart2Collapsed] = useState(false);
  const [chart3Collapsed, setChart3Collapsed] = useState(false);
  const [chart4Collapsed, setChart4Collapsed] = useState(false);
  const [mediaTab, setMediaTab] = useState('braking');
  const [heatmapDataKey, setHeatmapDataKey] = useState('speed');
  const [pageTab, setPageTab] = useState('analysis');

  const tracesRef = useRef(null);
  const trackMapRef = useRef(null);

  useEffect(() => {
    const el = tracesRef.current;
    if (!el) return;

    const syncWidth = () => {
      const width = el.getBoundingClientRect().width;
      if (width > 0) {
        setTracesWidth(Math.floor(width));
      }
    };

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w && w > 0) setTracesWidth(Math.floor(w));
    });

    syncWidth();
    ro.observe(el);
    window.addEventListener('resize', syncWidth);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncWidth);
    };
  }, []);

  useEffect(() => {
    const el = trackMapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w && w > 0) setTrackMapWidth(Math.floor(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const loadVideos = async (uid, fileName) => {
    try {
      const res = await fetch(`/api/session/${uid}/videos?fileName=${encodeURIComponent(fileName)}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        return data.videos || [];
      }
    } catch (err) {
      console.error('Error loading videos:', err);
    }
    return [];
  };

  useEffect(() => {
    loadSessionData();
    loadTrackData();
    loadCompareSessions();
    // Load videos for primary session
    const userId = session.userId || user.uid;
    if (session.fileName) {
      loadVideos(userId, session.fileName).then(setPrimaryVideos);
    } else {
      setPrimaryVideos([]);
    }
    setOverlayVideos([]);
  }, [session]);

  // Load the primary session's full telemetry data from the API.
  // For shared sessions, uses the authenticated /api/sharing/ endpoint.
  // For admin views (clicked from Admin Dashboard), uses /api/admin/session/.
  const loadSessionData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const fileName = session.fileName;
      const userId = session.userId || user.uid;

      if (!fileName) {
        setSessionData(null);
        setLoadError('Session file path is missing.');
        setLoading(false);
        return;
      }

      const isAdminView = session.isAdminView && userId !== user.uid;
      const isShared = !isAdminView && session.isShared && userId !== user.uid;
      let url;
      let headers = {};
      if (isAdminView) {
        url = `/api/admin/session/${userId}/${fileName}`;
        headers = authHeaders();
      } else if (isShared) {
        url = `/api/sharing/session/${userId}/${fileName}`;
        headers = authHeaders();
      } else {
        url = `/api/session/${userId}/${fileName}`;
        headers = authHeaders();
      }

      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setSessionData({
          sessionId: data.sessionId,
          deviceType: data.deviceType,
          track: data.trackName || session.track,
          car: data.car,
          date: data.date,
          laps: applySmoothedSamples(data.laps),
          notes: data.notes || '',
        });
        setNotes(data.notes || '');
        setSavedNotes(data.notes || '');
        setLoading(false);
        return;
      }

      // Demo/example sessions ship without a backing file — synthesize placeholder telemetry.
      if (session.isExample) {
        setSessionData(generateSessionData(session));
        setLoading(false);
        return;
      }

      setSessionData(null);
      setLoadError(`Failed to load session telemetry (HTTP ${res.status}).`);
    } catch (err) {
      console.error('Error loading session data:', err);
      setSessionData(null);
      setLoadError('Failed to load session telemetry.');
    } finally {
      setLoading(false);
    }
  };

  // Load GPS track coordinates for the track map and heatmap visualizations
  const loadTrackData = async () => {
    const trackId = session.trackId || session.track?.replace(/\s+/g, '_');
    if (!trackId) return;

    try {
      const res = await fetch(`/api/tracks/${trackId}`, { headers: authHeaders() });
      if (res.ok) {
        const track = await res.json();
        setTrackCoordinates(Array.isArray(track?.coordinates) ? track.coordinates : []);
        const meta = track?.metadata || {};
        setTrackMirror({ mirrorX: !!meta.mirrorX, mirrorY: !!meta.mirrorY });
      }
    } catch (err) {
      console.error('Error loading track data:', err);
      setTrackCoordinates([]);
    }
  };


  const maxSpeed = useMemo(() => sessionData ? getMaxSpeed(sessionData) : { value: 0, lapNum: null }, [sessionData]);
  const maxG = useMemo(() => sessionData ? getMaxG(sessionData) : { lateral: 0, long: 0, latLapNum: null, longLapNum: null }, [sessionData]);
  const gpsPointCount = useMemo(
    () => (Array.isArray(trackCoordinates) ? trackCoordinates.length : 0),
    [trackCoordinates]
  );

  // Median max-distance across all laps (used to detect incomplete laps).
  // Shared with the rest of the app via src/utils/bestLap.js.
  const medianLapDist = useMemo(() => getMedianLapDist(sessionData?.laps), [sessionData]);

  // Index of the fastest *complete* lap (rejects GPS-glitch and incomplete laps).
  // Uses the shared best-lap selector so the value matches the session tiles and
  // admin report (which compute it server-side from the same rules).
  const bestLapIdx = useMemo(
    () => findBestLapIndex(sessionData?.laps, medianLapDist),
    [sessionData, medianLapDist]
  );

  const bestLap = useMemo(() => sessionData?.laps?.[bestLapIdx] ?? null, [sessionData, bestLapIdx]);

  const segmentBands = useMemo(() => {
    if (!trackCoordinates?.length || !bestLap?.data?.length) return [];
    const segments = parseSegments(trackCoordinates);
    const bands = [];
    let searchStart = 0;
    for (const seg of segments) {
      const startCoord = trackCoordinates[seg.startIdx];
      const endCoord = trackCoordinates[seg.endIdx];
      const startMatch = findClosestSampleIndex(bestLap.data, startCoord.lat, startCoord.lng, searchStart);
      if (startMatch.index < 0) continue;
      const endMatch = findClosestSampleIndex(bestLap.data, endCoord.lat, endCoord.lng, startMatch.index + 1);
      if (endMatch.index < 0) continue;
      bands.push({
        label: seg.label,
        startDist: bestLap.data[startMatch.index].dist,
        endDist: bestLap.data[endMatch.index].dist,
      });
      searchStart = endMatch.index + 1;
    }
    return bands;
  }, [trackCoordinates, bestLap]);

  // Filter out outlier laps (in-laps, out-laps, aborted laps) that are more than
  // LAP_FILTER_THRESHOLD (30%) away from the best lap time, and incomplete laps
  // whose distance is less than 75% of the median. Returns original indices.
  const filteredLapIndexes = useMemo(() => {
    if (!sessionData?.laps?.length) return [];
    const bestTime = sessionData.laps[bestLapIdx]?.lapTime;
    if (!bestTime || bestTime <= 0) return sessionData.laps.map((_, i) => i);
    const lo = bestTime * (1 - LAP_FILTER_THRESHOLD);
    const hi = bestTime * (1 + LAP_FILTER_THRESHOLD);
    return sessionData.laps
      .map((lap, i) => i)
      .filter((i) => {
        const lap = sessionData.laps[i];
        const t = lap.lapTime;
        if (!(t > 0 && t >= lo && t <= hi)) return false;
        if (medianLapDist > 0 && lap.data?.length) {
          const maxDist = Math.max(...lap.data.map(p => p.dist || 0));
          if (maxDist < medianLapDist * 0.75) return false;
        }
        return true;
      });
  }, [sessionData, bestLapIdx, medianLapDist]);

  // The actual lap objects after filtering (this is what LapSelector displays)
  const filteredLaps = useMemo(() =>
    filteredLapIndexes.map((i) => sessionData?.laps[i]).filter(Boolean),
    [sessionData, filteredLapIndexes]
  );

  // Best lap's index within the filteredLaps array (not the raw session laps)
  const filteredBestLapIdx = useMemo(() =>
    filteredLapIndexes.indexOf(bestLapIdx),
    [filteredLapIndexes, bestLapIdx]
  );

  // Top 3 fastest laps within the filtered set (indices into filteredLaps)
  const topThreeLapIndexes = useMemo(() => {
    if (!filteredLaps.length) return [];
    return filteredLaps
      .map((lap, idx) => ({ idx, lapTime: lap?.lapTime ?? Number.POSITIVE_INFINITY }))
      .sort((a, b) => a.lapTime - b.lapTime)
      .slice(0, 3)
      .map((item) => item.idx);
  }, [filteredLaps]);

  // Auto-select top 3 laps (or best) when session data first loads
  useEffect(() => {
    if (filteredLaps.length) {
      setSelectedLaps(topThreeLapIndexes.length ? topThreeLapIndexes : [filteredBestLapIdx]);
    }
  }, [filteredLaps, filteredBestLapIdx, topThreeLapIndexes]);

  const handleToggleLap = useCallback((idx) => {
    startTransition(() => {
      setSelectedLaps((prev) => {
        if (prev.includes(idx)) {
          if (prev.length === 1) return prev;
          return prev.filter((i) => i !== idx);
        }
        return [...prev, idx];
      });
    });
  }, []);

  const handleCrosshairChange = useCallback((pos) => {
    setCrosshairPos(pos);
  }, []);

  const handleZoomChange = useCallback((range) => {
    setZoomRange(range);
    setCrosshairPos(null);
  }, []);

  const handleDragRangeChange = useCallback((range) => {
    setDragRange(range);
  }, []);

  const handleTraceViewStyleChange = useCallback((event) => {
    const nextStyle = event.target.value;
    setTraceViewStyle(nextStyle);
    setCrosshairPos(null);
    if (filteredLaps.length) {
      startTransition(() => {
        setSelectedLaps(topThreeLapIndexes.length ? topThreeLapIndexes : [filteredBestLapIdx]);
      });
    }
  }, [filteredLaps, topThreeLapIndexes, filteredBestLapIdx]);

  const handleSelectBest = useCallback(() => {
    startTransition(() => {
      setSelectedLaps(filteredBestLapIdx != null ? [filteredBestLapIdx] : []);
    });
    setCrosshairPos(null);
  }, [filteredBestLapIdx]);

  const handleSelectTopThree = useCallback(() => {
    startTransition(() => {
      setSelectedLaps(topThreeLapIndexes.length ? topThreeLapIndexes : (filteredBestLapIdx != null ? [filteredBestLapIdx] : []));
    });
    setCrosshairPos(null);
  }, [topThreeLapIndexes, filteredBestLapIdx]);

  const handleSelectAll = useCallback(() => {
    if (filteredLaps.length) {
      startTransition(() => {
        setSelectedLaps(filteredLaps.map((_, i) => i));
      });
      setCrosshairPos(null);
    }
  }, [filteredLaps]);

  // Load available sessions for comparison: own sessions + shared teammate sessions
  // at the SAME TRACK as the current session (different tracks cannot be compared)
  const loadCompareSessions = useCallback(async () => {
    setCompareLoading(true);
    const trackId = session.trackId || session.track?.replace(/\s+/g, '_');
    const all = [];

    // Own sessions at this track (exclude current)
    try {
      const res = await fetch(`/api/sessions/${user.uid}`, { headers: authHeaders() });
      if (res.ok) {
        const ownSessions = await res.json();
        for (const s of (Array.isArray(ownSessions) ? ownSessions : [])) {
          if (s.trackId === trackId && s.sessionId !== session.sessionId) {
            all.push({ ...s, ownerName: user.displayName || user.email, ownerUid: user.uid, profileImage: user.profileImage || '', isShared: false });
          }
        }
      }
    } catch (err) {
      console.error('Error loading own sessions for compare:', err);
    }

    // Shared sessions at this track
    try {
      const sharedUsers = await getSharedSessions(trackId);
      for (const u of sharedUsers) {
        for (const s of u.sessions) {
          all.push({ ...s, ownerName: u.displayName || u.email, ownerUid: u.userId, profileImage: u.profileImage || '', isShared: true });
        }
      }
    } catch (err) {
      console.error('Error loading shared sessions for compare:', err);
    }

    setCompareSessions(all);
    setCompareLoading(false);
  }, [session, user.uid]);

  // Load a comparison session and set both sessions to their best lap.
  // Uses ReactDOM.flushSync to batch all state updates into a single render,
  // preventing race conditions where intermediate renders could reset selections.
  const handleSelectOverlaySession = useCallback(async (compareSession) => {
    try {
      let data;
      if (compareSession.isShared) {
        data = await getSharedSessionData(compareSession.ownerUid, compareSession.fileName);
      } else {
        const res = await fetch(`/api/session/${compareSession.ownerUid}/${compareSession.fileName}`, { headers: authHeaders() });
        data = await res.json();
      }

      // Compute overlay best lap index before setting state
      let overlayBest = 0;
      if (data.laps?.length) {
        for (let i = 1; i < data.laps.length; i++) {
          if (data.laps[i].lapTime < data.laps[overlayBest].lapTime) overlayBest = i;
        }
      }

      let overlayDateLabel = formatDate(compareSession.date || '');
      const coord = trackCoordinates?.[0];
      if (coord && compareSession.createdAt) {
        const tz = getTimezone(coord.lat, coord.lng);
        const t = formatTimeAtTrack(compareSession.createdAt, tz);
        if (t) overlayDateLabel += ` ${t}`;
      }

      // Load overlay session videos
      if (compareSession.fileName) {
        loadVideos(compareSession.ownerUid, compareSession.fileName).then(setOverlayVideos);
      }

      // Set all state together to avoid race conditions between renders
      ReactDOM.flushSync(() => {
        setOverlaySessionData({ ...data, laps: applySmoothedSamples(data.laps || []), isShared: compareSession.isShared });
        setOverlayLabel(`${compareSession.ownerName} - ${overlayDateLabel}`);
        setSelectedOverlayLaps([overlayBest]);
      });
    } catch (err) {
      console.error('Error loading overlay session:', err);
    }
  }, []);

  const handleToggleOverlayLap = useCallback((idx) => {
    startTransition(() => {
      setSelectedOverlayLaps((prev) => {
        if (prev.includes(idx)) {
          return prev.filter((i) => i !== idx);
        }
        return [...prev, idx];
      });
    });
  }, []);

  const handleRemoveOverlay = useCallback(() => {
    setOverlaySessionData(null);
    setOverlayLabel('');
    setSelectedOverlayLaps([]);
    setOverlayVideos([]);
  }, []);

  // Best lap index within the overlay session (for the overlay LapSelector panel)
  const overlayBestLapIdx = useMemo(() => {
    if (!overlaySessionData?.laps?.length) return 0;
    let bestIdx = 0;
    for (let i = 1; i < overlaySessionData.laps.length; i++) {
      if (overlaySessionData.laps[i].lapTime < overlaySessionData.laps[bestIdx].lapTime) bestIdx = i;
    }
    return bestIdx;
  }, [overlaySessionData]);

  // Top 3 fastest laps within the overlay session
  const overlayTopThreeLapIndexes = useMemo(() => {
    if (!overlaySessionData?.laps?.length) return [];
    return overlaySessionData.laps
      .map((lap, idx) => ({ idx, lapTime: lap?.lapTime ?? Number.POSITIVE_INFINITY }))
      .sort((a, b) => a.lapTime - b.lapTime)
      .slice(0, 3)
      .map((item) => item.idx);
  }, [overlaySessionData]);

  const handleOverlaySelectBest = useCallback(() => {
    startTransition(() => {
      setSelectedOverlayLaps(overlayBestLapIdx != null ? [overlayBestLapIdx] : []);
    });
  }, [overlayBestLapIdx]);

  const handleOverlaySelectTopThree = useCallback(() => {
    startTransition(() => {
      setSelectedOverlayLaps(overlayTopThreeLapIndexes.length ? overlayTopThreeLapIndexes : (overlayBestLapIdx != null ? [overlayBestLapIdx] : []));
    });
  }, [overlayTopThreeLapIndexes, overlayBestLapIdx]);

  const handleOverlaySelectAll = useCallback(() => {
    if (overlaySessionData?.laps?.length) {
      startTransition(() => {
        setSelectedOverlayLaps(overlaySessionData.laps.map((_, i) => i));
      });
    }
  }, [overlaySessionData]);

  // Driver labels: extract first name for compact display in traces, split times, etc.
  // In compare mode, laps from different drivers show "Steve L1" vs "Jim L3".
  // In single-session mode, driver labels exist but are hidden (same driver for all laps).
  const primaryDriverLabel = session.isShared ? (session.ownerName || 'Shared') : (user.displayName || user.email);
  const firstName = (name) => (name || '').split(/[\s@-]/)[0] || name;

  // DISPLAY LAPS: The merged array of all laps to visualize.
  // - Primary laps: selected laps from filteredLaps, tagged with primary driver's name
  // - Overlay laps: selected laps from overlaySessionData, GPS-aligned via alignLapToReference(),
  //   tagged with overlay driver's name
  // This array is passed to TelemetryGraph, LapDelta, SplitTimeTable, CornerBreakdown, etc.
  const displayLaps = useMemo(() => {
    if (!filteredLaps.length) return [];
    const primary = selectedLaps.map((i) => filteredLaps[i]).filter(Boolean)
      .map((lap) => ({ ...lap, driverLabel: firstName(primaryDriverLabel) }));
    if (!overlaySessionData?.laps) return primary;
    const refLap = primary[0] || filteredLaps[0];
    const overlayFirstName = firstName(overlayLabel.split(' - ')[0]);
    const overlay = selectedOverlayLaps
      .map((i) => overlaySessionData.laps[i])
      .filter(Boolean)
      .map((lap) => ({ ...alignLapToReference(lap, refLap), driverLabel: overlayFirstName }));
    return [...primary, ...overlay];
  }, [filteredLaps, selectedLaps, overlaySessionData, selectedOverlayLaps, primaryDriverLabel, overlayLabel]);

  const splitTimeReport = useMemo(() => {
    if (!sessionData || !displayLaps.length) return null;

    return buildSplitTimeReport(
      {
        ...sessionData,
        laps: displayLaps,
      },
      trackCoordinates,
      { beginLap: 1 }
    );
  }, [sessionData, displayLaps, trackCoordinates]);

  const brakingAnalysis = useMemo(() => analyzeBraking({
    laps: displayLaps,
    trackCoordinates,
    distanceUnit: 'ft',
    sourcePreference: 'auto',
  }), [displayLaps, trackCoordinates]);

  const brakingCoachCards = useMemo(() => buildBrakingCoachCards({
    analysis: brakingAnalysis,
    referenceMode: brakingAnalysis.referenceMode,
    user,
  }), [brakingAnalysis, user]);

  const throttleAnalysis = useMemo(() => analyzeThrottle({
    laps: displayLaps,
    trackCoordinates,
    distanceUnit: 'ft',
  }), [displayLaps, trackCoordinates]);

  const throttleCoachCards = useMemo(() => buildThrottleCoachCards({
    analysis: throttleAnalysis,
    referenceMode: throttleAnalysis.referenceMode,
    user,
  }), [throttleAnalysis, user]);

  const segmentSpeedCoachCards = useMemo(() => buildSegmentSpeedCoachCards({
    laps: displayLaps,
    trackCoordinates,
    user,
  }), [displayLaps, trackCoordinates, user]);

  const segmentTimeCoachCards = useMemo(() => buildSegmentTimeCoachCards({
    report: splitTimeReport,
  }), [splitTimeReport]);

  // Compute car positions on track from crosshair — distance-based GPS lookup
  // Uses binary search by distance to match chart x-axis (distance-based, not index-based)
  const carPositions = useMemo(() => {
    if (crosshairPos == null || !displayLaps.length) return null;
    return displayLaps.map((lap, i) => {
      if (!lap?.data?.length) return null;
      const data = lap.data;
      const totalMaxDist = data[data.length - 1]?.dist || 1;
      let distStart = 0;
      let distEnd = totalMaxDist;
      if (zoomRange) {
        distStart = zoomRange.x0 * totalMaxDist;
        distEnd = zoomRange.x1 * totalMaxDist;
      }
      const targetDist = distStart + crosshairPos * (distEnd - distStart);
      // Binary search for closest point by distance
      let lo = 0, hi = data.length - 1;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (data[mid].dist < targetDist) lo = mid + 1;
        else hi = mid;
      }
      let idx = lo;
      if (idx > 0 && Math.abs(data[idx - 1].dist - targetDist) < Math.abs(data[idx].dist - targetDist)) {
        idx = idx - 1;
      }
      const pt = data[idx];
      if (!pt || pt.lat == null || pt.lng == null) return null;
      const nextPt = data[Math.min(idx + 1, data.length - 1)];
      return {
        lat: pt.lat,
        lng: pt.lng,
        nextLat: nextPt?.lat,
        nextLng: nextPt?.lng,
        color: TRACE_COLORS[i % TRACE_COLORS.length],
        point: pt,
      };
    }).filter(Boolean);
  }, [crosshairPos, displayLaps, zoomRange]);

  // Compute highlighted track points from drag selection range (use first lap for GPS)
  const highlightPoints = useMemo(() => {
    if (!dragRange || !displayLaps.length) return null;
    const lap = displayLaps[0];
    if (!lap?.data?.length) return null;
    const maxDist = lap.data[lap.data.length - 1]?.dist || 1;
    const distStart = dragRange.x0 * maxDist;
    const distEnd = dragRange.x1 * maxDist;
    const points = [];
    for (const pt of lap.data) {
      if (pt.dist >= distStart && pt.dist <= distEnd && pt.lat != null && pt.lng != null) {
        points.push({ lat: pt.lat, lng: pt.lng });
      }
    }
    return points.length > 0 ? points : null;
  }, [dragRange, displayLaps]);

  const focusBounds = useMemo(() => {
    if (!zoomRange || !displayLaps.length) return null;
    const lap = displayLaps[0];
    if (!lap?.data?.length) return null;
    const maxDist = lap.data[lap.data.length - 1]?.dist || 1;
    const distStart = zoomRange.x0 * maxDist;
    const distEnd = zoomRange.x1 * maxDist;
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const pt of lap.data) {
      if (pt.dist >= distStart && pt.dist <= distEnd && pt.lat != null && pt.lng != null) {
        if (pt.lat < minLat) minLat = pt.lat;
        if (pt.lat > maxLat) maxLat = pt.lat;
        if (pt.lng < minLng) minLng = pt.lng;
        if (pt.lng > maxLng) maxLng = pt.lng;
      }
    }
    if (!isFinite(minLat)) return null;
    return { minLat, maxLat, minLng, maxLng };
  }, [zoomRange, displayLaps]);

  // Detect which channels have data in this session
  const availableChannels = useMemo(() => {
    if (!sessionData?.laps?.length) return [];
    const sample = sessionData.laps[0].data.slice(0, 20);
    return getAllChannels(user).filter(ch =>
      ch.key !== 'speed' && sample.some(pt => pt[ch.key] != null)
    );
  }, [sessionData, user]);

  // Set default chart channel selections when available channels change
  const hasRichData = useMemo(() => {
    return availableChannels.some(ch => ch.key !== 'lateralG' && ch.key !== 'longG');
  }, [availableChannels]);

  useEffect(() => {
    if (!availableChannels.length) return;
    const has = (key) => availableChannels.some(ch => ch.key === key);
    if (hasRichData) {
      // Rich session (imports with rpm, throttle, brake, etc.)
      setChart2Key(prev => prev || (has('rpm') ? 'rpm' : has('lateralG') ? 'lateralG' : availableChannels[0]?.key));
      setChart3Key(prev => prev || (has('throttle') ? 'throttle' : has('longG') ? 'longG' : availableChannels[1]?.key || null));
      setChart4Key(prev => prev || (has('brake') ? 'brake' : null));
    } else {
      // Basic session (only speed + G-forces) — show just one G-force chart
      setChart2Key(prev => prev || (has('lateralG') ? 'lateralG' : availableChannels[0]?.key));
      setChart3Key(null);
      setChart4Key(null);
    }
  }, [availableChannels, hasRichData]);

  // Resolve channel objects from keys
  const chart2Channel = useMemo(() => chart2Key && CHANNELS[chart2Key] ? { key: chart2Key, ...CHANNELS[chart2Key] } : null, [chart2Key]);
  const chart3Channel = useMemo(() => chart3Key && CHANNELS[chart3Key] ? { key: chart3Key, ...CHANNELS[chart3Key] } : null, [chart3Key]);
  const chart4Channel = useMemo(() => chart4Key && CHANNELS[chart4Key] ? { key: chart4Key, ...CHANNELS[chart4Key] } : null, [chart4Key]);

  if (loading || !sessionData) {
    return (
      <div style={styles.page}>
        <Header user={user} onLogout={onLogout} onBack={onBack} showBack onAdmin={onAdmin} onDashboard={onDashboard} onProfile={onProfile} />
        {loadError ? (
          <div style={styles.loading}>{loadError}</div>
        ) : (
          <LoadingProgress label="Loading session data" expectedMs={5000} />
        )}
      </div>
    );
  }

  // LAP DELTA: Show time-difference graph when exactly 2 laps are displayed.
  // Works for both same-session comparison (2 laps from one session) and
  // cross-session comparison (1 primary + 1 overlay). The delta is computed by
  // interpolating time-at-distance for both laps over their overlapping range.
  const refLap = displayLaps.length === 2 ? displayLaps[0] : null;
  const cmpLap = displayLaps.length === 2 ? displayLaps[1] : null;

  const speedChannel = getChannel('speed', user);
  const isOwnSession = !session.isShared || session.userId === user.uid;

  const handleSaveNotes = async () => {
    setNotesSaving(true);
    try {
      const res = await fetch(`/api/session/${user.uid}/notes`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ fileName: session.fileName, notes }),
      });
      if (res.ok) {
        const data = await res.json();
        setSavedNotes(data.notes);
      }
    } catch (err) {
      console.error('Failed to save notes:', err);
    } finally {
      setNotesSaving(false);
    }
  };

  const visibleChartCount = 1
    + (chart2Channel && !chart2Collapsed ? 1 : 0)
    + (chart3Channel && !chart3Collapsed ? 1 : 0)
    + (chart4Channel && !chart4Collapsed ? 1 : 0)
    + (displayLaps.length === 2 ? 1 : 0);
  const trackMapHeight = Math.min(trackMapWidth, 400);

  const renderEmbeddedCoachSection = (title, cards, keyPrefix) => (
    <div style={styles.embeddedCoachSection}>
      <div style={styles.embeddedCoachTitle}>{title}</div>
      <div style={styles.coachingGrid}>
        {cards.length > 0 ? cards.map((card, index) => (
          <BrakingCoachCard
            key={`${keyPrefix}-${card.id}`}
            card={card}
            variant={index === 0 ? 'hero' : 'compact'}
          />
        )) : (
          <div style={styles.helperText}>
            No coaching insights are available for the selected laps yet.
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={styles.page} data-doc-id="analysis-page">
      <Header user={user} onLogout={onLogout} onBack={onBack} showBack onAdmin={onAdmin} onDashboard={onDashboard} onProfile={onProfile} />
      <div style={styles.content}>
        <div style={styles.sessionInfo}>
          <div>
            <div style={styles.trackName}>{session.track}</div>
            <div style={styles.carDate}>
              Vehicle: {session.car} &mdash; {formatDate(session.date)}
              {(() => {
                const coord = trackCoordinates?.[0];
                if (!coord || !session.createdAt) return null;
                const tz = getTimezone(coord.lat, coord.lng);
                const t = formatTimeAtTrack(session.createdAt, tz);
                return t ? ` ${t}` : null;
              })()}
              {session.sessionType && (
                <span style={{ margin: '0 8px', opacity: 0.4 }}>|</span>
              )}
              {session.sessionType && (
                <SessionTypeIcon sessionType={session.sessionType} size={14} />
              )}
            </div>
            {sessionData?.deviceType && (
              <div style={styles.carDate}>Device: {sessionData.deviceType}</div>
            )}
            {session.isShared && session.ownerName && (
              <div style={{ ...styles.carDate, color: '#2e86c1', fontWeight: 600 }}>
                Shared from: {session.ownerName}
              </div>
            )}
          </div>
          <button
            onClick={() => sessionData?.laps?.length && exportSessionCsv(session, sessionData)}
            disabled={!sessionData?.laps?.length}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              padding: '6px 14px',
              background: 'var(--color-surface-light, #1a2a3a)',
              border: '1px solid var(--color-border)',
              borderRadius: 6,
              color: sessionData?.laps?.length ? 'var(--color-text-dim)' : 'var(--color-text-muted, #555)',
              cursor: sessionData?.laps?.length ? 'pointer' : 'default',
              whiteSpace: 'nowrap',
            }}
          >
            Export CSV
          </button>
        </div>

        <div style={styles.statsRow} data-doc-id="analysis-stats">
          <StatCard label="Best Lap" value={formatLapTime(bestLap?.lapTime)} color="var(--color-accent)" details={bestLap?.lapNum != null ? [`Lap ${bestLap.lapNum}`] : null} />
          <StatCard label="Top Speed" value={formatSpeed(maxSpeed.value, user)} color="var(--color-accent)" details={maxSpeed.lapNum != null ? [`Lap ${maxSpeed.lapNum}`] : null} />
          <StatCard label="Max Lat G" value={formatG(maxG.lateral)} color="#e74c3c" details={maxG.latLapNum != null ? [`Lap ${maxG.latLapNum}`] : null} />
          <StatCard label="Max Long G" value={formatG(maxG.long)} color="#27ae60" details={maxG.longLapNum != null ? [`Lap ${maxG.longLapNum}`] : null} />
          <StatCard label="Laps" value={sessionData.laps.length} />
          <StatCard label="GPS Points" value={gpsPointCount} />
          {session.weather && (
            <StatCard
              label="Weather"
              value={formatTemp(session.weather.temperature)}
              color={session.weather.condition === 'clear' ? '#f9a825' : '#64b5f6'}
              details={[
                session.weather.label,
                `${session.weather.humidity}% humidity`,
                session.weather.windSpeed != null ? `${session.weather.windSpeed} mph wind` : null,
              ].filter(Boolean)}
              icon={<WeatherIcon condition={session.weather.condition} size={20} />}
            />
          )}
        </div>

        <div data-doc-id="analysis-page-tabs" style={{ ...styles.tabRow, marginTop: 12, marginBottom: 0 }}>
          {[{ id: 'analysis', label: 'Analysis', docId: 'analysis-analysis-tab' }, { id: 'voice', label: 'Voice Configuration', docId: 'analysis-voice-tab' }].map(tab => (
            <button
              key={tab.id}
              data-doc-id={tab.docId}
              onClick={() => setPageTab(tab.id)}
              style={{
                ...styles.tabButton,
                fontSize: 13,
                padding: '8px 20px',
                background: pageTab === tab.id ? 'var(--color-surface-light, #1a2a3a)' : 'transparent',
                border: pageTab === tab.id ? '1px solid var(--color-border)' : '1px solid transparent',
                color: pageTab === tab.id ? 'var(--color-text)' : 'var(--color-text-dim)',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {pageTab === 'voice' && (
          <VoiceConfigPanel
            trackId={session.trackId || session.track?.replace(/\s+/g, '_')}
            trackCoordinates={trackCoordinates}
            user={user}
          />
        )}

        {pageTab === 'analysis' && <div style={styles.mainLayout}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div data-doc-id="analysis-lap-selector">
              <LapSelector
                laps={filteredLaps}
                bestLapIdx={filteredBestLapIdx}
                selectedLaps={selectedLaps}
                onToggleLap={handleToggleLap}
                onSelectBest={handleSelectBest}
                onSelectTopThree={handleSelectTopThree}
                onSelectAll={handleSelectAll}
                primaryLabel={primaryDriverLabel}
                overlayLaps={overlaySessionData?.laps}
                overlayBestLapIdx={overlayBestLapIdx}
                selectedOverlayLaps={selectedOverlayLaps}
                onToggleOverlayLap={handleToggleOverlayLap}
                overlayLabel={overlayLabel}
                onRemoveOverlay={handleRemoveOverlay}
                sortOrder={lapSortOrder}
                onSortChange={setLapSortOrder}
                compareSessions={compareSessions}
                compareLoading={compareLoading}
                onSelectCompareSession={handleSelectOverlaySession}
                onLoadCompareSessions={loadCompareSessions}
                onOverlaySelectBest={handleOverlaySelectBest}
                onOverlaySelectTopThree={handleOverlaySelectTopThree}
                onOverlaySelectAll={handleOverlaySelectAll}
                overlayIsSameUser={!overlaySessionData?.isShared}
              />
            </div>
          </div>

          <div style={styles.analysisColumn}>
            <div style={styles.topRow}>
              <div style={styles.tracesColumn} ref={tracesRef}>
                <div style={styles.sectionHeaderRow} data-doc-id="analysis-telemetry">
                  <div style={{ ...styles.sectionTitle, marginBottom: 0, whiteSpace: 'nowrap' }}>Telemetry Traces</div>
                  <div style={styles.heightControlWrap}>
                    <label htmlFor="trace-view-style" style={styles.viewStyleLabel}>View Style</label>
                    <select
                      id="trace-view-style"
                      data-doc-id="analysis-trace-view-style"
                      value={traceViewStyle}
                      onChange={handleTraceViewStyleChange}
                      style={styles.viewStyleSelect}
                    >
                      <option value="standard">Standard</option>
                      <option value="candles">Candles</option>
                      <option value="ribbon">Ribbon</option>
                    </select>
                    <button
                      type="button"
                      title={xAxisMode === 'distance' ? 'Switch to time axis' : 'Switch to distance axis'}
                      onClick={() => {
                        setXAxisMode(m => m === 'distance' ? 'time' : 'distance');
                        setCrosshairPos(null);
                        setZoomRange(null);
                      }}
                      style={styles.xAxisToggleBtn}
                    >
                      {xAxisMode === 'distance' ? (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <rect x="1" y="5" width="14" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                          <line x1="4" y1="5" x2="4" y2="8" stroke="currentColor" strokeWidth="1"/>
                          <line x1="8" y1="5" x2="8" y2="9.5" stroke="currentColor" strokeWidth="1"/>
                          <line x1="12" y1="5" x2="12" y2="8" stroke="currentColor" strokeWidth="1"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                          <line x1="8" y1="4" x2="8" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <line x1="8" y1="8" x2="11" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      )}
                    </button>
                    <button
                      type="button"
                      data-doc-id="analysis-trace-reset"
                      onClick={() => {
                        setTraceSamplingMode('raw');
                        setCrosshairPos(null);
                        setZoomRange(null);
                        setXAxisMode('distance');
                      }}
                      style={styles.viewResetBtn}
                    >
                      Reset View
                    </button>
                  </div>
                </div>
                <div style={styles.graphsSection}>
                  {speedChannel && (
                    <ResizableChart defaultHeight={TRACE_HEIGHT}>
                      {(h) => (
                        <TelemetryGraph
                          laps={displayLaps}
                          channel={speedChannel}
                          trackCoordinates={trackCoordinates}
                          width={tracesWidth}
                          height={h}
                          samplingMode={traceSamplingMode}
                          viewStyle={xAxisMode === 'time' ? 'standard' : traceViewStyle}
                          crosshairPos={crosshairPos}
                          onCrosshairChange={handleCrosshairChange}
                          interactive
                          zoomRange={zoomRange}
                          onZoomChange={handleZoomChange}
                          onDragRangeChange={handleDragRangeChange}
                          segmentBands={segmentBands}
                          xAxisMode={xAxisMode}
                          user={user}
                        />
                      )}
                    </ResizableChart>
                  )}

                  {displayLaps.length === 2 && refLap && cmpLap && (
                    <ResizableChart defaultHeight={TRACE_HEIGHT}>
                      {(h) => (
                        <LapDelta
                          referenceLap={refLap}
                          comparisonLap={cmpLap}
                          crosshairPos={crosshairPos}
                          onCrosshairChange={handleCrosshairChange}
                          width={tracesWidth}
                          height={h}
                          samplingMode={traceSamplingMode}
                          viewStyle={xAxisMode === 'time' ? 'standard' : traceViewStyle}
                          zoomRange={zoomRange}
                          onZoomChange={handleZoomChange}
                          onDragRangeChange={handleDragRangeChange}
                          segmentBands={segmentBands}
                          xAxisMode={xAxisMode}
                          user={user}
                        />
                      )}
                    </ResizableChart>
                  )}

                  {chart2Channel && (
                    <ChartSlot
                      channel={chart2Channel}
                      selectableChannels={availableChannels}
                      onChannelChange={setChart2Key}
                      collapsed={chart2Collapsed}
                      onToggleCollapse={() => setChart2Collapsed(c => !c)}
                      defaultHeight={TRACE_HEIGHT}
                      laps={displayLaps}
                      trackCoordinates={trackCoordinates}
                      width={tracesWidth}
                      samplingMode={traceSamplingMode}
                      viewStyle={xAxisMode === 'time' ? 'standard' : traceViewStyle}
                      crosshairPos={crosshairPos}
                      onCrosshairChange={handleCrosshairChange}
                      zoomRange={zoomRange}
                      onZoomChange={handleZoomChange}
                      onDragRangeChange={handleDragRangeChange}
                      segmentBands={segmentBands}
                      xAxisMode={xAxisMode}
                      user={user}
                    />
                  )}
                  {chart3Channel && (
                    <ChartSlot
                      channel={chart3Channel}
                      selectableChannels={availableChannels}
                      onChannelChange={setChart3Key}
                      collapsed={chart3Collapsed}
                      onToggleCollapse={() => setChart3Collapsed(c => !c)}
                      defaultHeight={TRACE_HEIGHT}
                      laps={displayLaps}
                      trackCoordinates={trackCoordinates}
                      width={tracesWidth}
                      samplingMode={traceSamplingMode}
                      viewStyle={xAxisMode === 'time' ? 'standard' : traceViewStyle}
                      crosshairPos={crosshairPos}
                      onCrosshairChange={handleCrosshairChange}
                      zoomRange={zoomRange}
                      onZoomChange={handleZoomChange}
                      onDragRangeChange={handleDragRangeChange}
                      segmentBands={segmentBands}
                      xAxisMode={xAxisMode}
                      user={user}
                    />
                  )}
                  {chart4Channel && (
                    <ChartSlot
                      channel={chart4Channel}
                      selectableChannels={availableChannels}
                      onChannelChange={setChart4Key}
                      collapsed={chart4Collapsed}
                      onToggleCollapse={() => setChart4Collapsed(c => !c)}
                      defaultHeight={TRACE_HEIGHT}
                      laps={displayLaps}
                      trackCoordinates={trackCoordinates}
                      width={tracesWidth}
                      samplingMode={traceSamplingMode}
                      viewStyle={xAxisMode === 'time' ? 'standard' : traceViewStyle}
                      crosshairPos={crosshairPos}
                      onCrosshairChange={handleCrosshairChange}
                      zoomRange={zoomRange}
                      onZoomChange={handleZoomChange}
                      onDragRangeChange={handleDragRangeChange}
                      segmentBands={segmentBands}
                      xAxisMode={xAxisMode}
                      user={user}
                    />
                  )}
                </div>
              </div>
              <div ref={trackMapRef} style={styles.trackMapColumn} data-doc-id="analysis-track-column">
                <div style={{ ...styles.sectionTitle, textAlign: 'center' }}>Track Map</div>
                <div data-doc-id="analysis-track-map" style={{ position: 'sticky', top: 16 }}>
                  <TrackCanvas
                    coordinates={trackCoordinates}
                    width={trackMapWidth}
                    height={trackMapHeight}
                    carPositions={carPositions}
                    highlightPoints={highlightPoints}
                    focusBounds={focusBounds}
                    mirrorX={trackMirror.mirrorX}
                    mirrorY={trackMirror.mirrorY}
                  />
                </div>
              </div>
            </div>

                <div style={styles.fullWidthSection} data-doc-id="analysis-media">
                  {(() => {
                    const hasPrimaryVideo = primaryVideos.length > 0;
                    const hasOverlayVideo = overlayVideos.length > 0;
                    const primaryName = primaryDriverLabel.split(' ')[0] || 'Primary';
                    const overlayName = overlayLabel ? overlayLabel.split(' - ')[0].split(' ')[0] || 'Overlay' : '';
                    const heatmapChannel = getChannel(heatmapDataKey, user) || getChannel('speed', user);
                    const hasBrakingAnalysis = (brakingAnalysis?.corners?.length || 0) > 0;
                    const hasThrottleAnalysis = throttleAnalysis?.hasData && (throttleAnalysis?.corners?.length || 0) > 0;
                    const mediaTabs = [
                      ...(hasBrakingAnalysis ? [{ id: 'braking', label: `Braking (${brakingAnalysis.sourceLabel || 'Brake'})` }] : []),
                      ...(hasThrottleAnalysis ? [{ id: 'throttle', label: `Throttle (${throttleAnalysis.sourceLabel || 'TPS'})` }] : []),
                      { id: 'corner-breakdown', label: 'Segment Speeds' },
                      { id: 'split-times', label: 'Segment Times' },
                      ...(hasPrimaryVideo ? [{ id: 'video-primary', label: hasOverlayVideo ? `${primaryName} Video` : 'Session Video' }] : []),
                      { id: 'heatmap', label: `${heatmapChannel.label} Map` },
                      { id: 'satellite', label: 'Satellite Map' },
                      { id: 'gforce', label: 'Traction Circle' },
                      ...(hasOverlayVideo ? [{ id: 'video-overlay', label: `${overlayName} Video` }] : []),
                    ];
                    const activeMediaTab = mediaTabs.find(t => t.id === mediaTab) ? mediaTab : 'heatmap';
                    return (
                      <div style={styles.mediaCoachRow}>
                        <div style={styles.mediaPanel}>
                          <div style={styles.framedPanel}>
                            <div style={styles.tabRow} data-doc-id="analysis-media-tabs">
                              {mediaTabs.map((tab) => (
                                <button
                                  key={tab.id}
                                  onClick={() => setMediaTab(tab.id)}
                                  style={{
                                    ...styles.tabButton,
                                    background: activeMediaTab === tab.id ? 'var(--color-surface-light, #1a2a3a)' : 'transparent',
                                    border: activeMediaTab === tab.id ? '1px solid var(--color-border)' : '1px solid transparent',
                                    color: activeMediaTab === tab.id ? 'var(--color-text)' : 'var(--color-text-dim)',
                                  }}
                                >
                                  {tab.label}
                                </button>
                              ))}
                            </div>
                            {activeMediaTab === 'heatmap' && (
                              <div data-doc-id="analysis-heatmap">
                                <TrackHeatmap
                                  coordinates={trackCoordinates}
                                  laps={displayLaps}
                                  width={tracesWidth}
                                  carPositions={carPositions}
                                  focusBounds={focusBounds}
                                  availableChannels={availableChannels}
                                  dataKey={heatmapDataKey}
                                  onDataKeyChange={setHeatmapDataKey}
                                  user={user}
                                  mirrorX={trackMirror.mirrorX}
                                  mirrorY={trackMirror.mirrorY}
                                />
                              </div>
                            )}
                            {activeMediaTab === 'satellite' && (
                              <div data-doc-id="analysis-satellite-map">
                                <SatelliteMap
                                  laps={displayLaps}
                                  carPositions={carPositions}
                                  focusBounds={focusBounds}
                                  availableChannels={availableChannels}
                                  dataKey={heatmapDataKey}
                                  onDataKeyChange={setHeatmapDataKey}
                                />
                              </div>
                            )}
                            {activeMediaTab === 'gforce' && (
                              <div data-doc-id="analysis-gforce-tab" style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                                <div style={{ width: 500, maxWidth: '100%', aspectRatio: '1 / 1', flexShrink: 0 }}>
                                  <GForcePlot
                                    data={displayLaps[0]?.data || []}
                                    width={500}
                                    height={500}
                                    crosshairPos={crosshairPos}
                                  />
                                </div>
                              </div>
                            )}
                            {activeMediaTab === 'braking' && (
                              <div data-doc-id="analysis-braking">
                                <BrakingSummaryTable
                                  rows={brakingAnalysis.corners}
                                  sourceLabel={brakingAnalysis.sourceLabel}
                                  peakLabel={brakingAnalysis.peakLabel}
                                  peakUnit={brakingAnalysis.peakUnit}
                                  avgStartSpread={brakingAnalysis.sessionSummary?.avgStartSpread}
                                  referenceLabel={brakingAnalysis.referenceLabel}
                                  user={user}
                                />
                                {renderEmbeddedCoachSection('AI Coach Analysis', brakingCoachCards, 'embedded-braking')}
                              </div>
                            )}
                            {activeMediaTab === 'throttle' && (
                              <div data-doc-id="analysis-throttle">
                                <ThrottleSummaryTable
                                  rows={throttleAnalysis.corners}
                                  referenceLabel={throttleAnalysis.referenceLabel}
                                  sourceLabel={throttleAnalysis.sourceLabel}
                                  user={user}
                                />
                                {renderEmbeddedCoachSection('AI Coach Analysis', throttleCoachCards, 'embedded-throttle')}
                              </div>
                            )}
                            {activeMediaTab === 'split-times' && (
                              <div data-doc-id="analysis-split-times-tab">
                                <SplitTimeTable report={splitTimeReport} />
                                {renderEmbeddedCoachSection('AI Coach Analysis', segmentTimeCoachCards, 'embedded-segment-time')}
                              </div>
                            )}
                            {activeMediaTab === 'corner-breakdown' && (
                              <div data-doc-id="analysis-corner-breakdown-tab">
                                <CornerBreakdown
                                  trackCoordinates={trackCoordinates}
                                  laps={displayLaps}
                                  user={user}
                                />
                                {renderEmbeddedCoachSection('AI Coach Analysis', segmentSpeedCoachCards, 'embedded-segment-speed')}
                              </div>
                            )}
                            {activeMediaTab === 'video-primary' && (
                              <div data-doc-id="analysis-video">
                                <VideoPlayer
                                  primaryVideos={primaryVideos}
                                  overlayVideos={[]}
                                  overlayLabel=""
                                  hasOverlaySession={false}
                                />
                              </div>
                            )}
                            {activeMediaTab === 'video-overlay' && (
                              <div data-doc-id="analysis-video-overlay">
                                <VideoPlayer
                                  primaryVideos={overlayVideos}
                                  overlayVideos={[]}
                                  overlayLabel=""
                                  hasOverlaySession={false}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div style={styles.fullWidthSection} data-doc-id="analysis-notes">
                  <div style={styles.sectionTitle}>Notes</div>
                  <textarea
                    data-doc-id="analysis-notes-textarea"
                    value={notes}
                    onChange={isOwnSession ? (e) => setNotes(e.target.value) : undefined}
                    readOnly={!isOwnSession}
                    placeholder={isOwnSession ? 'Add notes about this session...' : ''}
                    maxLength={5000}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      minHeight: 80, padding: '10px 12px',
                      background: '#0D1B2A', color: '#c8dbe6',
                      border: '1px solid rgba(138,180,200,0.2)',
                      borderRadius: 6, fontFamily: 'inherit', fontSize: 13,
                      resize: 'vertical', opacity: isOwnSession ? 1 : 0.7,
                    }}
                  />
                  {isOwnSession && notes !== savedNotes && (
                    <button
                      type="button"
                      onClick={handleSaveNotes}
                      disabled={notesSaving}
                      data-doc-id="analysis-notes-save"
                      style={{
                        marginTop: 6, padding: '6px 16px',
                        background: 'rgba(46, 134, 193, 0.15)',
                        border: '1px solid rgba(46, 134, 193, 0.3)',
                        borderRadius: 6, color: '#2e86c1',
                        fontFamily: 'var(--font-heading)', fontSize: 11,
                        fontWeight: 700, letterSpacing: 1,
                        cursor: notesSaving ? 'wait' : 'pointer',
                      }}
                    >
                      {notesSaving ? 'Saving...' : 'Save Notes'}
                    </button>
                  )}
                </div>

          </div>
        </div>}

      </div>
    </div>
  );
}
